import React, { useEffect, useState } from 'react';
import { Button, Form, Row, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { getCorrectEnums } from '../../../utils/Functions';
import AutocompleteInput from '../../../shared/components/AutocompleteInput';
import SingleSelectDropdown, { ISingleSelectOption } from '../../../shared/components/SingleSelectDropdown';
import { downloadFileRequest } from '../../../api/api.service';
import { CAMPAIGN_LIST_DOWNLOAD_URL } from '../../../utils/Endpoints';
import { Company } from '../../../model/company/Company';
import { CompanyService } from '../../company/CompanyService';
import { IUser } from '../../../model/user/IUser';
import { FaTimes } from 'react-icons/fa';
import { ErrorToast } from '../../../utils/toasters';
import { IPackages } from '../../../model/package/IPackage';
import { PackagesService } from '../../packages/PackagesService';

interface ICampaignFilter {
    handleChange: any;
    filterParams: any;
    setFilterParams: any;
    handleClick: () => void;
    supervisors: IUser[];
}

export function CampaignFilter({
    handleChange,
    filterParams,
    setFilterParams,
    handleClick,
    supervisors,
}: ICampaignFilter) {
    const campaignStatus = useSelector((state: any) => state?.enums?.campaignStatus);
    const CampaignPhase = useSelector((state: any) => state?.enums?.CampaignPhaseEnum);

    const [userFilter, setUserFilter] = useState('');
    const [companyFilter, setCompanyFilter] = useState('');
    const [clientFilter, setClientFilter] = useState('');
    const [clientDropdown, setClientDropdown] = useState<Company[]>([]);
    const [companyDropdown, setCompanyDropdown] = useState<Company[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [inputTypes, setInputTypes] = useState({
        startFrom: 'text',
        startTo: 'text',
    });
    const [packagesList, setPackagesList] = useState<IPackages[]>([]);

    const renderSelect = (name: string, placeholder: string, options: ISingleSelectOption[], emptyValue = '') => (
        <SingleSelectDropdown
            placeholder={placeholder}
            options={options}
            selected={filterParams?.[name] && filterParams[name] !== emptyValue ? String(filterParams[name]) : ''}
            onChange={value => handleChange({ target: { name, value: value || emptyValue } })}
        />
    );

    const enumOptions = (values: string[] = []): ISingleSelectOption[] =>
        values.map(value => ({ value, label: getCorrectEnums(value ?? '') }));

    function handleTypes(evt: any, type: string) {
        setInputTypes(prev => ({
            ...prev,
            [evt.target.name]: type,
        }));
    }

    useEffect(() => {
        CompanyService.getCompanies({ displayNameOrId: companyFilter })
            .then(data => {
                setCompanyDropdown(data.data);
            })
            .catch(err => ErrorToast(err));

        PackagesService.getPackages({ page: 1, perPage: 99999999 })
            .then(data => {
                setPackagesList(data.data);
            })
            .catch(err => ErrorToast(err));
    }, [companyFilter]);

    useEffect(() => {
        CompanyService.getCompanies({ displayNameOrId: clientFilter })
            .then(data => {
                setClientDropdown(data.data);
            })
            .catch(err => ErrorToast(err));
        /**
         * @see https://github.com/rt2zz/redux-persist#persiststorestore-config-callback
         * @see https://github.com/rt2zz/redux-persist#persistor-object
         */
        // export const persistor = persistStore(store);
        //
    }, [clientFilter]);

    return (
        <Form>
            <Row style={{ rowGap: '18px' }}>
                <div className="col-md-2 col-6">
                    <Form.Control
                        className="form-control autocomplete-input mb-lg-0"
                        placeholder="Campaign ID"
                        name="id"
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <Form.Control
                        className="form-control autocomplete-input mb-lg-0"
                        placeholder="Campaign name"
                        name="title"
                        onChange={handleChange}
                    />
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'packageId',
                        'All Packages',
                        packagesList.map(item => ({ value: String(item.id), label: getCorrectEnums(item.displayName) }))
                    )}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect('status', 'All statuses', enumOptions(campaignStatus))}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect('types', 'All Types', [
                        { value: 'standard', label: 'Standard' },
                        { value: 'linked', label: 'Linked' },
                        { value: 'long-term', label: 'Long-term' },
                    ])}
                </div>
                <div className="col-md-2 col-6">
                    {renderSelect(
                        'isCompleted',
                        'Is Completed?',
                        [
                            { value: 'true', label: 'Completed' },
                            { value: 'null', label: 'Not Completed' },
                        ],
                        'undefined'
                    )}
                </div>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Company name or id"
                        onSelect={setFilterParams}
                        setFilter={setCompanyFilter}
                        keyState={'companyId'}
                        keyItemSelect={'id'}
                        value={companyFilter}
                        items={companyDropdown}
                        hasClearBtn
                        renderItems={['id', 'displayName']}
                        clearFunc={() => {
                            setFilterParams((prev: any) => ({ ...prev, companyId: undefined }));
                            setCompanyFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Client name or id"
                        onSelect={setFilterParams}
                        setFilter={setClientFilter}
                        keyState={'clientId'}
                        keyItemSelect={'id'}
                        value={clientFilter}
                        items={clientDropdown}
                        hasClearBtn
                        renderItems={['id', 'displayName']}
                        clearFunc={() => {
                            setFilterParams((prev: any) => ({ ...prev, clientId: undefined }));
                            setClientFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <AutocompleteInput
                        placeholder="Supervisor"
                        onSelect={setFilterParams}
                        setFilter={setUserFilter}
                        keyState={'supervisorId'}
                        keyItemSelect={'id'}
                        value={userFilter}
                        items={supervisors}
                        hasClearBtn
                        renderItems={['id', 'firstName', 'lastName']}
                        clearFunc={() => {
                            setFilterParams((prev: any) => ({ ...prev, supervisorId: undefined }));
                            setUserFilter('');
                        }}
                    />
                </div>
                <div className="col-md-2 col-6">
                    <div className="d-flex bg-secondary-o-100 border rounded">
                        <Form.Control
                            placeholder="Publication from:"
                            className="autocomplete-input border-0 cursor-pointer"
                            type={inputTypes.startFrom}
                            name="startFrom"
                            value={filterParams?.startFrom || ''}
                            onFocus={e => handleTypes(e, 'date')}
                            onBlur={e => handleTypes(e, 'text')}
                            onChange={handleChange}
                        />
                        {filterParams?.startFrom && (
                            <Button
                                variant=""
                                className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                onClick={() => {
                                    setFilterParams((prev: any) => ({ ...prev, startFrom: undefined }));
                                }}
                            >
                                <FaTimes fontSize={14} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-6">
                    <div className="d-flex bg-secondary-o-100 border rounded">
                        <Form.Control
                            placeholder="Publication to:"
                            className="autocomplete-input border-0 cursor-pointer"
                            type={inputTypes.startTo}
                            name="startTo"
                            value={filterParams?.startTo || ''}
                            onFocus={e => handleTypes(e, 'date')}
                            onBlur={e => handleTypes(e, 'text')}
                            onChange={handleChange}
                        />
                        {filterParams?.startTo && (
                            <Button
                                variant=""
                                className="py-2 px-3 clearBtn btn-hover-bg-secondary"
                                onClick={() => {
                                    setFilterParams((prev: any) => ({ ...prev, startTo: undefined }));
                                }}
                            >
                                <FaTimes fontSize={14} />
                            </Button>
                        )}
                    </div>
                </div>
                <div className="col-md-2 col-6">{renderSelect('phase', 'Phase', enumOptions(CampaignPhase))}</div>
            </Row>
            <div className="d-flex gap-2">
                <Button className="mt-4" disabled={isLoading} onClick={() => handleClick()}>
                    Search
                </Button>
                <Button
                    className="mt-4"
                    variant="outline-primary"
                    onClick={() =>
                        downloadFileRequest(
                            CAMPAIGN_LIST_DOWNLOAD_URL,
                            'export-budget',
                            null,
                            filterParams,
                            setIsLoading
                        )
                    }
                    disabled={isLoading}
                >
                    {isLoading ? (
                        <>
                            Generating excel... <Spinner animation="border" size="sm" role="status" className="ml-1" />
                        </>
                    ) : (
                        'Export current'
                    )}
                </Button>
            </div>
        </Form>
    );
}
